import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Allow only digits, one decimal point, and an optional leading minus
const sanitizeCoordInput = (raw) => {
  let s = String(raw || '').replace(/[^0-9.\-]/g, '');
  s = s.replace(/(?!^)-/g, '');
  const parts = s.split('.');
  if (parts.length > 2) s = `${parts.shift()}.${parts.join('')}`;
  return s;
};

function PropertyMaster({ token }) {
  const STANDALONE_CHAIN_CODE = 'NONCHAIN';
  const STANDALONE_CHAIN_NAME = 'Standalone Hotels';

  const emptyForm = {
    code: '',
    name: '',
    logo_url: '',
    country: '',
    currency_code: '',
    city: '',
    state: '',
    timezone: 'Asia/Kolkata',
    module_front_office_enabled: true,
    module_food_inventory_enabled: true,
    module_kot_pos_enabled: true,
    module_bar_enabled: true,
    module_housekeeping_enabled: false,
    module_banquet_enabled: false,
    module_banquet_crm_enabled: false,
    module_accounting_enabled: false,
    module_stores_enabled: false,
    module_guest_portal_enabled: false,
    module_guest_portal_nlp_enabled: false,
    module_ai_revenue_manager_enabled: false,
    module_ai_guest_sentiment_enabled: false,
    module_ai_noshow_predictor_enabled: false,
    module_ai_guest_recognition_enabled: false,
    module_ai_housekeeping_optimizer_enabled: false,
    module_ai_occupancy_forecast_enabled: false,
    module_ai_upsell_engine_enabled: false,
    module_ai_staff_assistant_enabled: false,
    module_ai_folio_anomaly_enabled: false,
    module_ai_corporate_risk_enabled: false,
    module_ai_banquet_lead_arbitration_enabled: false,
    module_rate_shopper_enabled: false,
    module_price_intelligence_enabled: false,
    module_revenue_manager_enabled: false,
    rate_shopper_max_syncs_per_day: 5,
    latitude: '',
    longitude: '',
    is_active: true,
    easeroom_hotel_id: ''
  };

  const [chains, setChains] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [selectedChainId, setSelectedChainId] = useState('');
  const [hotelType, setHotelType] = useState('chain');
  const [standaloneChainId, setStandaloneChainId] = useState('');
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [geoSearching, setGeoSearching] = useState(false);
  const [geoQuery, setGeoQuery] = useState('');
  // Transient "Copied" feedback for the property-UUID copy button.
  const [copiedId, setCopiedId] = useState('');
  const copyPropertyId = (id) => {
    const val = String(id || '');
    const done = () => { setCopiedId(val); setTimeout(() => setCopiedId(''), 1500); };
    try {
      if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(val).then(done).catch(done); }
      else { const ta = document.createElement('textarea'); ta.value = val; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); done(); }
    } catch (_) { done(); }
  };
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const searchNominatim = async (query) => {
    if (!query.trim()) return;
    setGeoSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query.trim())}&limit=1`, {
        headers: { 'Accept-Language': 'en' }
      });
      const data = await res.json();
      if (data && data.length > 0) {
        setForm(prev => ({ ...prev, latitude: data[0].lat, longitude: data[0].lon }));
      } else {
        setError('No location found for that address');
      }
    } catch { setError('Geocoding failed'); }
    finally { setGeoSearching(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError('Geolocation not supported by browser'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(prev => ({ ...prev, latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) })),
      () => setError('Location access denied')
    );
  };

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      // Non-JSON response (e.g. nginx 502/503 HTML page) — show a clean message
      const statusMsg = {
        502: 'Server is temporarily unavailable (502). Please try again shortly.',
        503: 'Service unavailable (503). Please try again shortly.',
        504: 'Server timed out (504). Please try again.',
      }[res.status] || `Unexpected server response (HTTP ${res.status})`;
      return { data: {}, message: statusMsg };
    }
  };

  const loadChains = async () => {
    try {
      const res = await fetch('/api/v1/core/chains', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) {
        setError(message || (res.status === 401 || res.status === 403 ? 'Session expired. Please login again.' : `Failed to load chains (HTTP ${res.status})`));
        return [];
      }
      const rows = Array.isArray(data) ? data : [];
      setChains(rows);
      const standalone = rows.find((c) => String(c.code || '').toUpperCase() === STANDALONE_CHAIN_CODE);
      setStandaloneChainId(standalone?.id || '');
      if (!selectedChainId && rows.length > 0) {
        const firstRegular = rows.find((c) => String(c.code || '').toUpperCase() !== STANDALONE_CHAIN_CODE);
        setSelectedChainId((firstRegular || rows[0]).id);
      }
      return rows;
    } catch {
      setError('Network error while loading chains');
      return [];
    }
  };

  const loadProperties = async (chainId) => {
    if (!chainId) {
      setProperties([]);
      return;
    }
    try {
      const res = await fetch(`/api/v1/core/properties?chain_id=${encodeURIComponent(chainId)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-chain-id': chainId
        }
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) {
        setError(message || (res.status === 401 || res.status === 403 ? 'Session expired. Please login again.' : `Failed to load properties (HTTP ${res.status})`));
        return;
      }
      setProperties(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(`Network error while loading properties${e?.message ? `: ${e.message}` : ''}`);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await fetch('/api/v1/core/countries', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) {
        setError(message || `Failed to load countries (HTTP ${res.status})`);
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      setCountries(rows);
    } catch (e) {
      setError(`Network error while loading countries${e?.message ? `: ${e.message}` : ''}`);
    }
  };

  const loadCurrencies = async () => {
    try {
      const res = await fetch('/api/v1/core/currencies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) {
        setError(message || `Failed to load currencies (HTTP ${res.status})`);
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      setCurrencies(rows);
    } catch (e) {
      setError(`Network error while loading currencies${e?.message ? `: ${e.message}` : ''}`);
    }
  };

  const loadTimezones = async () => {
    try {
      const res = await fetch('/api/v1/core/timezones', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { data } = await parseResponse(res);
      setTimezones(Array.isArray(data) ? data : []);
    } catch {
      setTimezones([]);
    }
  };

  useEffect(() => {
    loadChains();
    loadCountries();
    loadCurrencies();
    loadTimezones();
  }, [token]);

  useEffect(() => {
    if (!chains.length) return;
    const preferredChainId = localStorage.getItem('hotelConfigSelectedChainId');
    if (preferredChainId && chains.some((c) => c.id === preferredChainId && String(c.code || '').toUpperCase() !== STANDALONE_CHAIN_CODE)) {
      setSelectedChainId(preferredChainId);
    }
  }, [chains]);

  const ensureStandaloneChain = async () => {
    const existing = chains.find((c) => String(c.code || '').toUpperCase() === STANDALONE_CHAIN_CODE);
    if (existing?.id) {
      setStandaloneChainId(existing.id);
      return existing.id;
    }

    const res = await fetch('/api/v1/core/chains', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ code: STANDALONE_CHAIN_CODE, name: STANDALONE_CHAIN_NAME })
    });
    const { data, message } = await parseResponse(res);
    if (!res.ok) {
      if (String(message || '').toLowerCase().includes('already exists')) {
        const refreshedChains = await loadChains();
        const refreshed = refreshedChains.find((c) => String(c.code || '').toUpperCase() === STANDALONE_CHAIN_CODE);
        if (refreshed?.id) {
          setStandaloneChainId(refreshed.id);
          return refreshed.id;
        }
      }
      throw new Error(message || 'Failed to prepare standalone hotel setup');
    }
    if (data?.id) {
      setChains((prev) => [...prev, data]);
      setStandaloneChainId(data.id);
      return data.id;
    }
    throw new Error('Standalone chain was not created');
  };

  const getEffectiveChainId = () => (hotelType === 'standalone' ? standaloneChainId : selectedChainId);

  useEffect(() => {
    const effectiveChainId = getEffectiveChainId();
    loadProperties(effectiveChainId);
    setSearchQuery('');
    setCurrentPage(1);
  }, [selectedChainId, standaloneChainId, hotelType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  useEffect(() => {
    if (selectedChainId && hotelType === 'chain') {
      localStorage.setItem('hotelConfigSelectedChainId', selectedChainId);
    }
  }, [selectedChainId, hotelType]);

  const handleSave = async () => {
    let effectiveChainId = getEffectiveChainId();
    if (hotelType === 'standalone' && !effectiveChainId) {
      try {
        effectiveChainId = await ensureStandaloneChain();
      } catch (e) {
        setError(e?.message || 'Failed to prepare standalone chain');
        return;
      }
    }
    if (!effectiveChainId) {
      setError('Please select a chain for chain hotel');
      return;
    }
    const easeroomId = (form.easeroom_hotel_id || '').trim();
    if (easeroomId && !/^\d{4,5}$/.test(easeroomId)) {
      setError('EaseRoom Hotel ID must be 4 or 5 digits');
      return;
    }
    const cityStr = (form.city || '').trim();
    const stateStr = (form.state || '').trim();
    if (cityStr && /^\d/.test(cityStr)) {
      setError('City cannot start with a number');
      return;
    }
    if (stateStr && /^\d/.test(stateStr)) {
      setError('State cannot start with a number');
      return;
    }
    const latStr = (form.latitude || '').trim();
    const lonStr = (form.longitude || '').trim();
    if ((latStr && !lonStr) || (!latStr && lonStr)) {
      setError('Please provide both Latitude and Longitude (or leave both empty)');
      return;
    }
    let latNum = null;
    let lonNum = null;
    if (latStr) {
      latNum = Number(latStr);
      if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
        setError('Latitude must be a number between -90 and 90');
        return;
      }
    }
    if (lonStr) {
      lonNum = Number(lonStr);
      if (!Number.isFinite(lonNum) || lonNum < -180 || lonNum > 180) {
        setError('Longitude must be a number between -180 and 180');
        return;
      }
    }
    // Pricing Intelligence Suite: Rate Shopper is the base but is not sold on
    // its own — it must be bundled with at least one of Price Intelligence or
    // Revenue Manager (valid combos: RS+PI, RS+RMS, RS+RMS+PI).
    if (form.module_rate_shopper_enabled === true
        && form.module_price_intelligence_enabled !== true
        && form.module_revenue_manager_enabled !== true) {
      setError('Rate Shopper must be bundled with Price Intelligence and/or Revenue Manager (RMS) — it cannot be enabled on its own.');
      return;
    }
    const payload = {
      code: (form.code || '').trim(),
      name: (form.name || '').trim(),
      logo_url: (form.logo_url || '').trim() || null,
      country: (form.country || '').trim(),
      currency_code: (form.currency_code || '').trim(),
      city: (form.city || '').trim(),
      state: (form.state || '').trim(),
      timezone: (form.timezone || '').trim() || 'Asia/Kolkata',
      module_front_office_enabled: form.module_front_office_enabled !== false,
      module_food_inventory_enabled: form.module_food_inventory_enabled !== false,
      module_kot_pos_enabled: form.module_kot_pos_enabled !== false,
      module_bar_enabled: form.module_bar_enabled !== false,
      module_housekeeping_enabled: form.module_housekeeping_enabled === true,
      module_banquet_enabled: form.module_banquet_enabled === true,
      module_banquet_crm_enabled: form.module_banquet_crm_enabled === true,
      module_accounting_enabled: form.module_accounting_enabled === true,
      module_stores_enabled: form.module_stores_enabled === true,
      module_guest_portal_enabled: form.module_guest_portal_enabled === true,
      module_guest_portal_nlp_enabled: form.module_guest_portal_enabled === true && form.module_guest_portal_nlp_enabled === true,
      module_ai_revenue_manager_enabled: form.module_ai_revenue_manager_enabled === true,
      module_ai_guest_sentiment_enabled: form.module_ai_guest_sentiment_enabled === true,
      module_ai_noshow_predictor_enabled: form.module_ai_noshow_predictor_enabled === true,
      module_ai_guest_recognition_enabled: form.module_ai_guest_recognition_enabled === true,
      module_ai_housekeeping_optimizer_enabled: form.module_ai_housekeeping_optimizer_enabled === true,
      module_ai_occupancy_forecast_enabled: form.module_ai_occupancy_forecast_enabled === true,
      module_ai_upsell_engine_enabled: form.module_ai_upsell_engine_enabled === true,
      module_ai_staff_assistant_enabled: form.module_ai_staff_assistant_enabled === true,
      module_ai_folio_anomaly_enabled: form.module_ai_folio_anomaly_enabled === true,
      module_ai_corporate_risk_enabled: form.module_ai_corporate_risk_enabled === true,
      module_ai_banquet_lead_arbitration_enabled: form.module_banquet_crm_enabled === true && form.module_ai_banquet_lead_arbitration_enabled === true,
      module_rate_shopper_enabled: form.module_rate_shopper_enabled === true,
      module_price_intelligence_enabled: form.module_rate_shopper_enabled === true && form.module_price_intelligence_enabled === true,
      module_revenue_manager_enabled: form.module_rate_shopper_enabled === true && form.module_revenue_manager_enabled === true,
      rate_shopper_max_syncs_per_day: Math.max(1, Math.round(Number(form.rate_shopper_max_syncs_per_day) || 5)),
      latitude: latNum,
      longitude: lonNum,
      is_active: form.is_active,
      easeroom_hotel_id: easeroomId || null
    };
    if (!payload.code || !payload.name || !payload.country || !payload.currency_code) {
      setError('Code, Name, Country and Currency are required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/v1/core/properties/${editingId}` : '/api/v1/core/properties', {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-chain-id': effectiveChainId
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 413) setError('Logo image is too large — please use a smaller image');
        else setError(data.error || `Failed to save property (${res.status})`);
        return;
      }
      setError('');
      setForm(emptyForm);
      setEditingId(null);
      loadProperties(effectiveChainId);
    } catch (e) {
      setError('Network error while saving property — please check your connection');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      code: row.code || '',
      name: row.name || '',
      logo_url: row.logo_url || '',
      country: row.country || '',
      currency_code: row.currency_code || '',
      city: row.city || '',
      state: row.state || '',
      timezone: row.timezone || 'Asia/Kolkata',
      module_front_office_enabled: row.module_front_office_enabled !== false,
      module_food_inventory_enabled: row.module_food_inventory_enabled !== false,
      module_kot_pos_enabled: row.module_kot_pos_enabled !== false,
      module_bar_enabled: row.module_bar_enabled !== false,
      module_housekeeping_enabled: Boolean(row.module_housekeeping_enabled),
      module_banquet_enabled: Boolean(row.module_banquet_enabled),
      module_banquet_crm_enabled: Boolean(row.module_banquet_crm_enabled),
      module_accounting_enabled: Boolean(row.module_accounting_enabled),
      module_stores_enabled: Boolean(row.module_stores_enabled),
      module_guest_portal_enabled: Boolean(row.module_guest_portal_enabled),
      module_guest_portal_nlp_enabled: Boolean(row.module_guest_portal_nlp_enabled),
      module_ai_revenue_manager_enabled: Boolean(row.module_ai_revenue_manager_enabled),
      module_ai_guest_sentiment_enabled: Boolean(row.module_ai_guest_sentiment_enabled),
      module_ai_noshow_predictor_enabled: Boolean(row.module_ai_noshow_predictor_enabled),
      module_ai_guest_recognition_enabled: Boolean(row.module_ai_guest_recognition_enabled),
      module_ai_housekeeping_optimizer_enabled: Boolean(row.module_ai_housekeeping_optimizer_enabled),
      module_ai_occupancy_forecast_enabled: Boolean(row.module_ai_occupancy_forecast_enabled),
      module_ai_upsell_engine_enabled: Boolean(row.module_ai_upsell_engine_enabled),
      module_ai_staff_assistant_enabled: Boolean(row.module_ai_staff_assistant_enabled),
      module_ai_folio_anomaly_enabled: Boolean(row.module_ai_folio_anomaly_enabled),
      module_ai_corporate_risk_enabled: Boolean(row.module_ai_corporate_risk_enabled),
      module_ai_banquet_lead_arbitration_enabled: Boolean(row.module_ai_banquet_lead_arbitration_enabled),
      module_rate_shopper_enabled: Boolean(row.module_rate_shopper_enabled),
      module_price_intelligence_enabled: Boolean(row.module_price_intelligence_enabled),
      module_revenue_manager_enabled: Boolean(row.module_revenue_manager_enabled),
      rate_shopper_max_syncs_per_day: row.rate_shopper_max_syncs_per_day != null ? row.rate_shopper_max_syncs_per_day : 5,
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
      is_active: Boolean(row.is_active),
      easeroom_hotel_id: row.easeroom_hotel_id || ''
    });
    setError('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/v1/core/properties/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-chain-id': getEffectiveChainId()
        }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to deactivate property');
        return;
      }
      setError('');
      loadProperties(getEffectiveChainId());
    } catch {
      setError('Network error while deleting property');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Logo file is too large (max 10 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // Compress using Canvas before storing as base64 — keeps payload < 200 KB
      const img = new Image();
      img.onload = () => {
        const MAX = 600;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.82);
        setForm((prev) => ({ ...prev, logo_url: compressed }));
        setError('');
      };
      img.onerror = () => setError('Failed to process image');
      img.src = String(reader.result || '');
    };
    reader.onerror = () => setError('Failed to read image file');
    reader.readAsDataURL(file);
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Property Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ marginRight: '0.6rem', fontWeight: 600 }}>Hotel Type:</label>
        <select
          value={hotelType}
          onChange={(e) => {
            setHotelType(e.target.value);
            setEditingId(null);
            setForm(emptyForm);
            setError('');
          }}
          style={{ padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #dcdde1', marginRight: '0.75rem' }}
        >
          <option value="chain">Chain Hotel</option>
          <option value="standalone">Standalone Hotel (No Chain)</option>
        </select>

        {hotelType === 'chain' && (
          <>
            <label style={{ marginRight: '0.6rem', fontWeight: 600 }}>Chain:</label>
            <select
              value={selectedChainId}
              onChange={(e) => setSelectedChainId(e.target.value)}
              style={{ padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            >
              {chains
                .filter((chain) => String(chain.code || '').toUpperCase() !== STANDALONE_CHAIN_CODE)
                .map((chain) => (
                  <option key={chain.id} value={chain.id}>
                    {chain.code} - {chain.name}
                  </option>
                ))}
            </select>
          </>
        )}
      </div>

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '1.5rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Property' : 'Create Property'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <input
            placeholder="Property Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="Property Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden' }}>
              <span style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#4a5568', fontWeight: 600, flexShrink: 0 }}>Choose File</span>
              <span style={{ fontSize: '0.82rem', color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {form.logo_url ? 'Logo selected ✓' : 'Upload property logo/photo'}
              </span>
              <input type="file" accept="image/*" onChange={(e) => handleLogoFile((e.target.files || [])[0])} style={{ display: 'none' }} />
            </label>
            {form.logo_url && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <img src={form.logo_url} alt="Property logo preview" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #dcdde1' }} />
                <button type="button" onClick={() => setForm({ ...form, logo_url: '' })} className="btn-danger"
                  style={{ padding: '0.3rem 0.7rem', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                  Remove
                </button>
              </div>
            )}
          </div>
          <select
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          >
            <option value="">Select Country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.country_name}>
                {c.country_name}
              </option>
            ))}
          </select>
          <select
            value={form.currency_code}
            onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          >
            <option value="">Select Currency</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.currency_code}>
                {c.currency_code}{c.currency_name ? ` - ${c.currency_name}` : ''}
              </option>
            ))}
          </select>
          <input
            placeholder="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="State"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-text)' }}>
              Timezone
            </label>
            {editingId && form.timezone && (
              <span style={{
                display: 'block',
                fontSize: '0.72rem',
                fontWeight: 500,
                overflowWrap: 'anywhere',
                color: form.timezone.includes('/') ? '#16a34a' : '#dc2626'
              }}>
                {form.timezone.includes('/')
                  ? `(current: ${form.timezone} ✓)`
                  : `(current: ${form.timezone} — invalid, please reselect ⚠)`}
              </span>
            )}
          <select
            value={form.timezone || ''}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', minWidth: 0 }}
          >
            <option value="">-- Select Timezone --</option>
            {(() => {
              // Group by country; surface the property's country at the top so the
              // most likely match is one click away, but every backend timezone is
              // still selectable below.
              const byCountry = {};
              timezones.forEach((tz) => {
                const c = tz.country || 'Other';
                if (!byCountry[c]) byCountry[c] = [];
                byCountry[c].push(tz);
              });
              const ordered = Object.keys(byCountry).sort();
              if (form.country && byCountry[form.country]) {
                ordered.splice(ordered.indexOf(form.country), 1);
                ordered.unshift(form.country);
              }
              return ordered.map((c) => (
                <optgroup key={c} label={c}>
                  {byCountry[c].map((tz) => (
                    <option key={tz.id} value={tz.tz_name}>
                      {tz.display_name || tz.tz_name}
                    </option>
                  ))}
                </optgroup>
              ));
            })()}
            {form.timezone && !timezones.some((tz) => tz.tz_name === form.timezone) && (
              <option value={form.timezone}>{form.timezone} (legacy)</option>
            )}
          </select>
          </div>
          <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-text)' }}>EaseRoom Hotel ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional, 4–5 digits)</span></label>
            <input
              placeholder="e.g. 12345"
              value={form.easeroom_hotel_id}
              onChange={(e) => setForm({ ...form, easeroom_hotel_id: e.target.value.replace(/\D/g, '').slice(0, 5) })}
              maxLength={5}
              inputMode="numeric"
              style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--theme-border)', marginTop: 4, paddingTop: 8, fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Location (Latitude / Longitude)</div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <input
              placeholder="Search address..."
              value={geoQuery}
              onChange={(e) => setGeoQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchNominatim(geoQuery); } }}
              style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1', minWidth: 0 }}
            />
            <button type="button" onClick={() => searchNominatim(geoQuery)} disabled={geoSearching}
              style={{ padding: '0.65rem 1rem', borderRadius: '6px', border: 'none', background: geoSearching ? 'var(--theme-text-muted)' : '#3498db', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>
              {geoSearching ? 'Searching...' : 'Find'}
            </button>
            <button type="button" onClick={useMyLocation}
              style={{ padding: '0.65rem 1rem', borderRadius: '6px', border: '1px solid #dcdde1', background: 'var(--theme-bg-light)', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, color: '#475569' }}>
              Use My Location
            </button>
          </div>
          <input
            placeholder="Latitude"
            inputMode="decimal"
            value={form.latitude}
            onChange={(e) => setForm({ ...form, latitude: sanitizeCoordInput(e.target.value) })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="Longitude"
            inputMode="decimal"
            value={form.longitude}
            onChange={(e) => setForm({ ...form, longitude: sanitizeCoordInput(e.target.value) })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_front_office_enabled}
              onChange={(e) => setForm({ ...form, module_front_office_enabled: e.target.checked })}
            />
            PMS / Front Office Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_food_inventory_enabled}
              onChange={(e) => setForm({ ...form, module_food_inventory_enabled: e.target.checked })}
            />
            Food Inventory Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_kot_pos_enabled}
              onChange={(e) => setForm({ ...form, module_kot_pos_enabled: e.target.checked })}
            />
            KOT & POS Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_bar_enabled}
              onChange={(e) => setForm({ ...form, module_bar_enabled: e.target.checked })}
            />
            Bar Management Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_housekeeping_enabled}
              onChange={(e) => setForm({ ...form, module_housekeeping_enabled: e.target.checked })}
            />
            Housekeeping Module Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_banquet_enabled}
              onChange={(e) => setForm({ ...form, module_banquet_enabled: e.target.checked })}
            />
            Banquet Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_banquet_crm_enabled}
              onChange={(e) => setForm({ ...form, module_banquet_crm_enabled: e.target.checked, module_ai_banquet_lead_arbitration_enabled: e.target.checked ? form.module_ai_banquet_lead_arbitration_enabled : false })}
            />
            Banquet with CRM Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 24, opacity: form.module_banquet_crm_enabled ? 1 : 0.4 }}>
            <input
              type="checkbox"
              checked={form.module_ai_banquet_lead_arbitration_enabled}
              disabled={!form.module_banquet_crm_enabled}
              onChange={(e) => setForm({ ...form, module_ai_banquet_lead_arbitration_enabled: e.target.checked })}
            />
            Banquet AI (Lead Arbitration) — paid add-on
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_accounting_enabled}
              onChange={(e) => setForm({ ...form, module_accounting_enabled: e.target.checked })}
            />
            Accounting Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_stores_enabled}
              onChange={(e) => setForm({ ...form, module_stores_enabled: e.target.checked })}
            />
            Stores Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_guest_portal_enabled}
              onChange={(e) => setForm({ ...form, module_guest_portal_enabled: e.target.checked, module_guest_portal_nlp_enabled: e.target.checked ? form.module_guest_portal_nlp_enabled : false })}
            />
            Guest Digital Portal Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 24, opacity: form.module_guest_portal_enabled ? 1 : 0.4 }}>
            <input
              type="checkbox"
              checked={form.module_guest_portal_nlp_enabled}
              disabled={!form.module_guest_portal_enabled}
              onChange={(e) => setForm({ ...form, module_guest_portal_nlp_enabled: e.target.checked })}
            />
            Guest Portal NLP/Voice Assist
          </label>
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--theme-border)', marginTop: 4, paddingTop: 8, fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>AI Features</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_ai_revenue_manager_enabled}
              onChange={(e) => setForm({ ...form, module_ai_revenue_manager_enabled: e.target.checked })}
            />
            AI Revenue Manager
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_ai_guest_sentiment_enabled}
              onChange={(e) => setForm({ ...form, module_ai_guest_sentiment_enabled: e.target.checked })}
            />
            AI Guest Sentiment
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_ai_noshow_predictor_enabled}
              onChange={(e) => setForm({ ...form, module_ai_noshow_predictor_enabled: e.target.checked })}
            />
            AI No-Show Predictor
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_ai_guest_recognition_enabled}
              onChange={(e) => setForm({ ...form, module_ai_guest_recognition_enabled: e.target.checked })}
            />
            AI Guest Recognition
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={form.module_ai_housekeeping_optimizer_enabled}
              onChange={(e) => setForm({ ...form, module_ai_housekeeping_optimizer_enabled: e.target.checked })}
            />
            AI HK Optimizer
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={form.module_ai_occupancy_forecast_enabled}
              onChange={(e) => setForm({ ...form, module_ai_occupancy_forecast_enabled: e.target.checked })}
            />
            AI Occ Forecast
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.module_ai_upsell_engine_enabled}
              onChange={(e) => setForm({ ...form, module_ai_upsell_engine_enabled: e.target.checked })} />
            AI Upsell
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.module_ai_staff_assistant_enabled}
              onChange={(e) => setForm({ ...form, module_ai_staff_assistant_enabled: e.target.checked })} />
            AI Staff Asst
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.module_ai_folio_anomaly_enabled}
              onChange={(e) => setForm({ ...form, module_ai_folio_anomaly_enabled: e.target.checked })} />
            AI Folio Anomaly
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.module_ai_corporate_risk_enabled}
              onChange={(e) => setForm({ ...form, module_ai_corporate_risk_enabled: e.target.checked })} />
            AI Corp Risk
          </label>
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--theme-border)', marginTop: 4, paddingTop: 8, fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
            Pricing Intelligence Suite <span style={{ fontWeight: 400, color: '#94a3b8' }}>— paid add-ons</span>
            <div style={{ fontWeight: 400, fontSize: '0.78rem', color: form.module_rate_shopper_enabled && !form.module_price_intelligence_enabled && !form.module_revenue_manager_enabled ? '#dc2626' : '#94a3b8', marginTop: 2 }}>
              Rate Shopper is the base and must be bundled with Price Intelligence and/or Revenue Manager (RMS) — it cannot be sold on its own.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.module_rate_shopper_enabled}
              onChange={(e) => setForm({ ...form, module_rate_shopper_enabled: e.target.checked, module_price_intelligence_enabled: e.target.checked ? form.module_price_intelligence_enabled : false, module_revenue_manager_enabled: e.target.checked ? form.module_revenue_manager_enabled : false })}
            />
            Rate Shopper
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 24, opacity: form.module_rate_shopper_enabled ? 1 : 0.4 }}>
            <input
              type="checkbox"
              checked={form.module_price_intelligence_enabled}
              disabled={!form.module_rate_shopper_enabled}
              onChange={(e) => setForm({ ...form, module_price_intelligence_enabled: e.target.checked })}
            />
            Price Intelligence
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 24, opacity: form.module_rate_shopper_enabled ? 1 : 0.4 }}>
            <input
              type="checkbox"
              checked={form.module_revenue_manager_enabled}
              disabled={!form.module_rate_shopper_enabled}
              onChange={(e) => setForm({ ...form, module_revenue_manager_enabled: e.target.checked })}
            />
            Revenue Manager (RMS)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 24, opacity: form.module_rate_shopper_enabled ? 1 : 0.4 }}>
            Max manual syncs / day
            <input
              type="number" min="1" max="50" style={{ width: 60, padding: '0.25rem 0.4rem' }}
              value={form.rate_shopper_max_syncs_per_day}
              disabled={!form.module_rate_shopper_enabled}
              onChange={(e) => setForm({ ...form, rate_shopper_max_syncs_per_day: e.target.value })}
            />
            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>How many times this hotel's own "Run Rate Shop now" button can be clicked per day</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update Property' : 'Create Property'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Properties</h3>

      {(() => {
        const q = searchQuery.trim().toLowerCase();
        const filteredProperties = q
          ? properties.filter((p) =>
              String(p.name || '').toLowerCase().includes(q) ||
              String(p.code || '').toLowerCase().includes(q) ||
              String(p.city || '').toLowerCase().includes(q)
            )
          : properties;
        const suggestions = q
          ? properties.filter((p) => String(p.name || '').toLowerCase().includes(q)).slice(0, 8)
          : [];
        const totalPages = Math.max(1, Math.ceil(filteredProperties.length / pageSize));
        const safePage = Math.min(currentPage, totalPages);
        const startIdx = (safePage - 1) * pageSize;
        const pagedProperties = filteredProperties.slice(startIdx, startIdx + pageSize);

        return (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div style={{ position: 'relative', width: '280px' }}>
                <input
                  placeholder="Search hotel name, code or city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #dcdde1', boxSizing: 'border-box' }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                    background: '#fff', border: '1px solid #dcdde1', borderRadius: '6px',
                    marginTop: '2px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', maxHeight: '260px', overflowY: 'auto'
                  }}>
                    {suggestions.map((p) => (
                      <div
                        key={p.id}
                        onMouseDown={() => { setSearchQuery(p.name); setShowSuggestions(false); }}
                        style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                      >
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{p.code} · {p.city || '-'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} className="btn-muted" style={{ padding: '0.5rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}{q ? ` matching "${searchQuery}"` : ''}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.82rem', color: '#64748b' }}>Rows per page:</label>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #dcdde1' }}>
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {filteredProperties.length === 0 ? (
              <p>{properties.length === 0 ? 'No properties found' : 'No properties match your search'}</p>
            ) : (
              <>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Property ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Logo</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Country</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Currency</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>City</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>State</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Timezone</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Front Office</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Food Inv</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>KOT+POS</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Bar</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Housekeeping</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Banquet</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Banquet CRM</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Accounting</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Stores</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Guest Portal</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>NLP</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Rev Mgr</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Sentiment</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI No-Show</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Guest Rec</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI HK Opt</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Occ Fcst</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Upsell</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Staff</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Folio</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>AI Corp Risk</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Banquet AI</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rate Shopper</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Price Intel</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Revenue Mgr</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Lat</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Lng</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>EaseRoom ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {pagedProperties.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                <td style={{ padding: '0.75rem' }}>{row.code}</td>
                <td style={{ padding: '0.75rem' }}>{row.name}</td>
                <td style={{ padding: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <code style={{ fontSize: '0.72rem', color: '#475569', fontFamily: 'monospace' }} title={String(row.id)}>{String(row.id).slice(0, 8)}…</code>
                    <button type="button" onClick={() => copyPropertyId(row.id)} title="Copy full property ID (UUID)"
                      style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {copiedId === String(row.id) ? 'Copied ✓' : 'Copy'}
                    </button>
                  </div>
                </td>
                <td style={{ padding: '0.75rem' }}>
                  {row.logo_url ? (
                    <img src={row.logo_url} alt={`${row.name} logo`} style={{ width: '42px', height: '42px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #dcdde1' }} />
                  ) : '-'}
                </td>
                <td style={{ padding: '0.75rem' }}>{row.country || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.currency_code || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.city || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.state || '-'}</td>
                <td style={{ padding: '0.75rem' }}>
                  {row.timezone ? (
                    <span style={{
                      color: row.timezone.includes('/') ? 'inherit' : '#dc2626',
                      fontWeight: row.timezone.includes('/') ? 400 : 700
                    }}
                      title={row.timezone.includes('/') ? '' : 'Invalid IANA timezone — click Edit to fix'}>
                      {row.timezone}{row.timezone.includes('/') ? '' : ' ⚠'}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ padding: '0.75rem' }}>{row.module_front_office_enabled !== false ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_food_inventory_enabled !== false ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_kot_pos_enabled !== false ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_bar_enabled !== false ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_housekeeping_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_banquet_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_banquet_crm_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_accounting_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_stores_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_guest_portal_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_guest_portal_nlp_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_revenue_manager_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_guest_sentiment_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_noshow_predictor_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_guest_recognition_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_housekeeping_optimizer_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_occupancy_forecast_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_upsell_engine_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_staff_assistant_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_folio_anomaly_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_corporate_risk_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_ai_banquet_lead_arbitration_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_rate_shopper_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_price_intelligence_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem' }}>{row.module_revenue_manager_enabled ? 'Enabled' : 'Disabled'}</td>
                <td style={{ padding: '0.75rem', fontSize: '0.82rem' }}>{row.latitude != null ? Number(row.latitude).toFixed(5) : '-'}</td>
                <td style={{ padding: '0.75rem', fontSize: '0.82rem' }}>{row.longitude != null ? Number(row.longitude).toFixed(5) : '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.easeroom_hotel_id || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', cursor: 'pointer' }}>Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Showing {startIdx + 1}–{Math.min(startIdx + pageSize, filteredProperties.length)} of {filteredProperties.length}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <button type="button" onClick={() => setCurrentPage(1)} disabled={safePage === 1}
                      style={{ padding: '0.4rem 0.65rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', cursor: safePage === 1 ? 'default' : 'pointer', opacity: safePage === 1 ? 0.5 : 1 }}>«</button>
                    <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
                      style={{ padding: '0.4rem 0.65rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', cursor: safePage === 1 ? 'default' : 'pointer', opacity: safePage === 1 ? 0.5 : 1 }}>‹ Prev</button>
                    <span style={{ fontSize: '0.85rem', color: '#334155', padding: '0 0.4rem' }}>Page {safePage} of {totalPages}</span>
                    <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                      style={{ padding: '0.4rem 0.65rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', cursor: safePage === totalPages ? 'default' : 'pointer', opacity: safePage === totalPages ? 0.5 : 1 }}>Next ›</button>
                    <button type="button" onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}
                      style={{ padding: '0.4rem 0.65rem', borderRadius: '6px', border: '1px solid #dcdde1', background: '#fff', cursor: safePage === totalPages ? 'default' : 'pointer', opacity: safePage === totalPages ? 0.5 : 1 }}>»</button>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}
    </div>
  );
}

export default PropertyMaster;
